import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowRightStartOnRectangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

import { HandDisplay } from '../components/problems/HandDisplay';
import { VulnerabilityIndicator } from '../components/problems/VulnerabilityIndicator';
import { OvercallingAuctionDisplay } from '../components/overcalling-data-editing';
import {
  AssignDifficultyControls,
  AssignConfirmationMessage,
  DifficultyIndicator,
} from '../components/overcalling-assign-difficulty';
import { useOvercallingAssignDifficultySession } from '../hooks/useOvercallingAssignDifficultySession';
import { usePageTitle } from '../hooks/usePageTitle';
import { overcallingAssignDifficultyApi } from '../api/overcallingAssignDifficultyApi';
import type { AssignDifficultyCandidateItem } from '../types/overcallingAssignDifficulty';
import type { Hand as PracticeHand } from '../types/problems';
import { formatBidToken } from '../utils/formatBidToken';

import './OvercallingAssignDifficultyPage.css';

// Full direction names for display (mirrors ProblemsPage and the 009 page).
const DIRECTION_NAMES: Record<'N' | 'E' | 'S' | 'W', string> = {
  N: 'North',
  E: 'East',
  S: 'South',
  W: 'West',
};

const formatJeffBid = formatBidToken;

// Overcalling — Assign Level of Difficulty page. Mirrors the 009
// OvercallingDataEditingPage shape (header + Data category filter + hand-display
// + auction-display + answer container + Previous/Next), but replaces the
// Keep/Don't-Keep action row with the new AssignDifficultyControls and adds the
// DifficultyIndicator beside the "From" section.
// Introduced by feature 012-overcalling-assign-difficulty.
export function OvercallingAssignDifficultyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Hyphen, not an em dash: matches this page's own <h1>, the same way
  // "Ask Jeff - Review" and "3rd level bids - Review" title themselves.
  usePageTitle('Overcalling - Assign Level of Difficulty — Hawkes Bridge');

  const {
    filter,
    items,
    currentItem,
    counterTotal,
    counterPosition,
    loading,
    assigning,
    autoAdvancing,
    error,
    confirmationMessage,
    assignError,
    flashKey,
    flashRequested,
    loadCandidates,
    applyAssign,
    advanceNext,
    advancePrev,
  } = useOvercallingAssignDifficultySession();

  const [directItem, setDirectItem] = useState<AssignDifficultyCandidateItem | null>(null);
  const [directLoading, setDirectLoading] = useState(false);
  const [directError, setDirectError] = useState<string | null>(null);
  const [directAssigning, setDirectAssigning] = useState(false);
  const [directConfirmation, setDirectConfirmation] = useState<string | null>(null);
  const [directAssignError, setDirectAssignError] = useState<string | null>(null);
  const [directAssignSeq, setDirectAssignSeq] = useState(0);
  const [directFlashRequested, setDirectFlashRequested] = useState(false);

  const idParam = searchParams.get('id');

  const [showDetailsPopup, setShowDetailsPopup] = useState(false);

  useEffect(() => {
    if (!idParam) {
      setDirectItem(null);
      setDirectError(null);
      return;
    }
    setDirectLoading(true);
    setDirectError(null);
    setDirectConfirmation(null);
    setDirectAssignError(null);
    setDirectFlashRequested(false);
    setDirectAssignSeq(0);
    overcallingAssignDifficultyApi.getByTableResultId(Number(idParam))
      .then(item => setDirectItem(item))
      .catch(err => {
        if ((err as { response?: { status?: number } })?.response?.status === 404) {
          setDirectError(`Hand with ID ${idParam} is not in the assign-difficulty pool.`);
        } else {
          setDirectError(err instanceof Error ? err.message : 'Failed to load item.');
        }
      })
      .finally(() => setDirectLoading(false));
  }, [idParam]);

  const activeItem: AssignDifficultyCandidateItem | null = directItem ?? currentItem;
  const showNotFound = idParam && !directLoading && !directItem && directError;

  const pageScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    pageScrollRef.current?.scrollTo(0, 0);
  }, [activeItem?.tableResultId]);

  const jeffSeat = (activeItem?.jeffSeat as 'N' | 'E' | 'S' | 'W') || 'S';

  const handleExit = () => navigate('/data-editing');

  // In-flight + loading disables every interactive control per the Edge Case rules. The
  // post-Assign auto-advance window (autoAdvancing) is also a disabled state — the timed
  // advance must not be interrupted, exactly as on the 009 Data Editing page.
  const inFlight = loading || assigning || autoAdvancing || directLoading || directAssigning;

  // Non-wrapping navigation (Session 2026-05-29): disable Previous on the first hand and Next
  // on the last hand so the working set no longer wraps end-to-end.
  const atFirstHand = counterPosition <= 1;
  const atLastHand = counterTotal === 0 || counterPosition >= counterTotal;
  const prevDisabled = inFlight || !!directItem || atFirstHand;
  const nextDisabled = inFlight || !!directItem || atLastHand;

  // Direct-link Assign uses the page-local state so the indicator updates +
  // confirmation message render without going through the working-set hook.
  const onAssign = directItem
    ? async (level: number) => {
        if (!activeItem) return;
        setDirectAssigning(true);
        setDirectAssignError(null);
        setDirectConfirmation(null);
        setDirectFlashRequested(false);
        try {
          const record = await overcallingAssignDifficultyApi.assignDifficulty(activeItem.tableResultId, level);
          setDirectItem({
            ...activeItem,
            difficultyLevel: record.difficultyLevel,
            lastUpdatedBy: record.lastUpdatedBy,
            lastUpdatedAt: record.lastUpdatedAt,
          });
          setDirectConfirmation("'Level of difficulty' assigned.");
          setDirectAssignSeq(prev => prev + 1);
          setDirectFlashRequested(true);
        } catch (err) {
          setDirectAssignError(err instanceof Error ? err.message : 'Failed to assign difficulty');
        } finally {
          setDirectAssigning(false);
        }
      }
    : applyAssign;

  const onNext = directItem ? () => { /* no list in direct-link mode */ } : advanceNext;
  const onPrev = directItem ? () => { /* no list in direct-link mode */ } : advancePrev;

  // Composite flash key for the indicator: in working-set mode, the hook owns
  // it; in direct-link mode the page owns it.
  const indicatorFlashKey = directItem
    ? `${activeItem?.tableResultId ?? 'none'}-${directAssignSeq}`
    : flashKey;
  const indicatorFlashRequested = directItem ? directFlashRequested : flashRequested;
  const indicatorConfirmation = directItem ? directConfirmation : confirmationMessage;
  const indicatorAssignError = directItem ? directAssignError : assignError;

  if (loading && items.length === 0 && !activeItem) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-ink-soft">Loading hands...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={pageScrollRef}
      className="mx-auto px-2 md:px-4 py-8 short:py-3 narrow:py-1 problems-scroll problems-scroll-height scrollable"
      style={{ maxWidth: 'min(100vw - 2rem, 75rem)' }}
    >
      {/* Desktop header: title left; counter, Exit right. */}
      <div className="hidden md:flex narrow:hidden items-center gap-4 short:gap-2 mb-3 short:mb-2">
        <h1 className="text-lg sm:text-xl font-bold text-ink">
          Overcalling - Assign Level of Difficulty
        </h1>
        <div className="ml-auto flex items-center gap-4 short:gap-2">
          {!idParam && (
            <div className="flex items-center bg-cream border border-line rounded-lg px-3 py-1.5 gap-3">
              <span className="text-sm text-ink-soft">
                Hand: {counterPosition}/{counterTotal}
              </span>
            </div>
          )}
          <button
            type="button"
            onClick={handleExit}
            className="text-sm font-medium text-ink bg-cream border border-brass hover:bg-paper rounded-md px-3 py-1.5"
            aria-label="Exit"
          >
            Exit
          </button>
        </div>
      </div>

      {/* Desktop: Data category filter + Refresh (FR-007 / FR-008). */}
      {!idParam && (
        <div className="hidden md:flex narrow:hidden items-center gap-2 mb-4 short:mb-2">
          <label htmlFor="assign-difficulty-filter-desktop" className="text-sm font-bold text-ink">
            Data category:
          </label>
          <select
            id="assign-difficulty-filter-desktop"
            value={filter}
            onChange={e => loadCandidates(e.target.value as typeof filter)}
            disabled={inFlight}
            className="border border-line-strong rounded-md px-2 py-1 text-sm"
          >
            <option value="All">All</option>
            <option value="Assigned">Assigned</option>
            <option value="NotAssigned">Not Assigned</option>
          </select>
          <button
            type="button"
            onClick={() => loadCandidates(filter)}
            disabled={inFlight}
            className="text-sm font-medium text-ink bg-cream hover:bg-paper disabled:opacity-50 disabled:cursor-not-allowed border border-line-strong rounded-md px-3 py-1"
            aria-label="Refresh"
          >
            Refresh
          </button>
        </div>
      )}

      {/* Mobile compact controls row. */}
      <div className="md:hidden narrow:flex flex items-center gap-2 mb-1 overflow-hidden" data-testid="compact-controls">
        {!idParam ? (
          <span className="text-sm">
            <span className="font-bold text-ink">{counterPosition}/{counterTotal}</span>
          </span>
        ) : (
          activeItem && (
            <span className="text-sm font-bold text-ink">
              ID: {activeItem.tableResultId}
            </span>
          )
        )}
        <div className="w-px h-4 bg-line-strong" />
        {activeItem && (
          <button type="button" onClick={() => setShowDetailsPopup(prev => !prev)} className="text-ink-soft hover:text-ink-soft p-0.5" aria-label="Item details">
            <InformationCircleIcon className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={handleExit}
          className="ml-auto flex-shrink-0 p-1 text-ink-soft hover:text-ink hover:bg-cream rounded"
          aria-label="Exit"
          title="Exit"
        >
          <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Mobile filter. */}
      {!idParam && (
        <div className="md:hidden narrow:flex items-center gap-2 mb-2">
          <label htmlFor="assign-difficulty-filter-mobile" className="text-sm font-bold text-ink">
            Data category:
          </label>
          <select
            id="assign-difficulty-filter-mobile"
            value={filter}
            onChange={e => loadCandidates(e.target.value as typeof filter)}
            disabled={inFlight}
            className="border border-line-strong rounded-md px-2 py-1 text-sm"
          >
            <option value="All">All</option>
            <option value="Assigned">Assigned</option>
            <option value="NotAssigned">Not Assigned</option>
          </select>
          <button
            type="button"
            onClick={() => loadCandidates(filter)}
            disabled={inFlight}
            className="text-xs font-medium text-ink bg-cream hover:bg-paper disabled:opacity-50 disabled:cursor-not-allowed border border-line-strong rounded-md px-2 py-1"
            aria-label="Refresh"
          >
            Refresh
          </button>
        </div>
      )}

      {showDetailsPopup && activeItem && (
        <div className="md:hidden narrow:block fixed inset-0 z-40 bg-black/30 flex items-start justify-center pt-16 px-4" onClick={() => setShowDetailsPopup(false)}>
          <div className="bg-card rounded-lg shadow-lg p-4 w-full max-w-sm relative" onClick={e => e.stopPropagation()}>
            <button type="button" onClick={() => setShowDetailsPopup(false)} className="absolute top-1 right-1 text-ink-soft hover:text-ink-soft text-sm font-bold px-1" aria-label="Close">&times;</button>
            <h3 className="text-sm font-semibold text-ink mb-2">Item Details</h3>
            <div className="space-y-1 text-sm text-ink">
              <div><span className="text-ink-soft">Number:</span> <span className="font-medium">{activeItem.tableResultId}</span></div>
              <div><span className="text-ink-soft">Seat:</span> <span className="font-medium">{activeItem.jeffSeat || '—'}</span></div>
              <div><span className="text-ink-soft">Scoring:</span> <span className="font-medium">{activeItem.scoringType || 'N/A'}</span></div>
              {activeItem.sourceLabel && (
                <div><span className="text-ink-soft">From:</span> <span className="font-medium">{activeItem.sourceLabel}</span></div>
              )}
              {activeItem.vulnerability && (
                <div><span className="text-ink-soft">Vulnerability:</span> <span className="font-medium">{activeItem.vulnerability}</span></div>
              )}
              <div>
                <span className="text-ink-soft">Difficulty:</span>{' '}
                <span className="font-medium">
                  {activeItem.difficultyLevel === null ? 'N/A' : `${activeItem.difficultyLevel}/10`}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && <div className="text-red-700 text-sm mb-2">{error}</div>}

      {showNotFound && (
        <div className="p-4 border border-amber-300 bg-amber-50 rounded">
          <p className="text-amber-900">{directError}</p>
          <Link to="/overcalling-assign-difficulty" className="mt-2 inline-block text-primary-600 hover:text-primary-800">
            Back to default view
          </Link>
        </div>
      )}

      {!showNotFound && activeItem && (
        <>
          {/* Metadata row — adds the DifficultyIndicator beside the From section. */}
          <div className="hidden md:block narrow:hidden mt-6 short:mt-3 mb-4 short:mb-3">
            <div className="bg-card border border-line rounded-lg p-2 short:p-1.5 flex flex-wrap items-center gap-4 short:gap-3 text-sm short:text-xs">
              <span>
                <span className="text-ink-soft">Number (ID):</span>{' '}
                <span className="font-semibold text-ink">{activeItem.tableResultId}</span>
              </span>
              <span className="text-red-500 font-bold">
                <span>Scoring:</span>{' '}
                <span>{activeItem.scoringType || 'N/A'}</span>
              </span>
              {activeItem.sourceLabel && (
                <span className="hidden md:inline narrow:hidden">
                  <span className="text-ink-soft">From:</span>{' '}
                  <span className="font-medium text-ink">{activeItem.sourceLabel}</span>
                </span>
              )}
              <DifficultyIndicator
                level={activeItem.difficultyLevel}
                flashKey={indicatorFlashKey}
                flashRequested={indicatorFlashRequested}
              />
            </div>
          </div>

          {/* Mobile metadata row carries the DifficultyIndicator inline. */}
          <div className="md:hidden narrow:hidden mb-2">
            <DifficultyIndicator
              level={activeItem.difficultyLevel}
              flashKey={indicatorFlashKey}
              flashRequested={indicatorFlashRequested}
            />
          </div>

          {/* Main content - same gray-100 wrapper as the 009 page. `relative` anchors the
              post-Assign auto-advance mask overlay (below) that covers this whole container. */}
          <div className="relative bg-cream rounded-lg p-4 short:p-3 narrow:p-2 mb-3 short:mb-1 mobile-flex-col">
            <div className="flex flex-col flex-1 min-h-0 narrow:justify-start narrow:items-center lphone-outer">
              <div className="flex flex-wrap flex-1 min-h-0 gap-3 short:gap-2 narrow:gap-1.5 justify-center items-stretch narrow:flex-col narrow:flex-nowrap narrow:items-center narrow:justify-start lphone-items problem-items">
                <div className="flex gap-3 short:gap-2 narrow:gap-1.5 items-center flex-shrink-0 vuln-hand-group" data-seat={jeffSeat}>
                  <div className="flex-shrink-0">
                    <VulnerabilityIndicator
                      vulnerability={activeItem.vulnerability ?? 'None'}
                      decisionSeat={jeffSeat}
                      dealer={activeItem.fullAuction[0]?.seat as 'N' | 'E' | 'S' | 'W' | undefined}
                    />
                  </div>
                  <div className="bg-card border border-line rounded-lg p-2 short:p-1.5 flex-shrink-0 w-[clamp(9rem,40vw,11rem)] md:w-[clamp(7rem,16.5vw,9rem)] lg:w-[clamp(8rem,18vw,12rem)] lphone-youhold">
                    <h3 className="font-semibold text-ink short:h-[1.75rem] short:text-[0.95rem]" style={{ fontSize: '1rem' }}>
                      Jeff holds <span className="text-ink-soft font-normal">(<span className="lg:hidden">{jeffSeat}</span><span className="hidden lg:inline">{DIRECTION_NAMES[jeffSeat]}</span>)</span>
                    </h3>
                    <HandDisplay hand={activeItem.jeffHand as PracticeHand} />
                  </div>
                </div>

                <div className="bg-card border border-line rounded-lg p-2 short:p-1.5 flex flex-col auction-container-width">
                  <h3 className="font-semibold text-ink mb-1 short:mb-0.5 auction-header" style={{ fontSize: '1rem' }}>The Auction</h3>
                  <div className="auction-scroll scrollable flex-1">
                    {activeItem.fullAuction.length > 0 ? (
                      <OvercallingAuctionDisplay
                        fullAuction={activeItem.fullAuction}
                        jeffSeq={activeItem.jeffSeq}
                        vulnerability={activeItem.vulnerability ?? 'None'}
                        decisionSeat={jeffSeat}
                      />
                    ) : (
                      <div className="text-sm text-ink-soft">Auction not available.</div>
                    )}
                  </div>
                </div>

                {/* Answer container — reveals Jeff's actual bid (009 FR-025 behaviour). */}
                <div className="border rounded-lg p-3 short:p-2 self-stretch narrow:flex-auto narrow:min-h-36 narrow:flex narrow:flex-col narrow:self-auto feedback-container-width bg-card border-line">
                  <div className="font-bold text-ink text-base short:text-sm">
                    Jeff&apos;s bid
                  </div>
                  <div className="mt-2 short:mt-1 text-lg short:text-base font-semibold text-ink">
                    {formatJeffBid(activeItem.jeffBid)}
                  </div>
                </div>
              </div>
            </div>

            {/* Post-Assign auto-advance mask (Session 2026-05-29, mirrors 009 FR-030a): while the
                timed advance is pending, cover the entire gray container with a dark mask and show
                the assigned level plus "Moving to next hand ..." in large text, so it's obvious
                what happened and that the page is about to move on. Only fires in working-set
                mode — direct-link (?id=) Assign never auto-advances, so `autoAdvancing` stays
                false there. */}
            {autoAdvancing && (
              <div
                className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-[rgba(255,253,246,0.85)]"
                data-testid="auto-advance-mask"
                aria-live="polite"
              >
                <AssignConfirmationMessage level={activeItem.difficultyLevel} />
              </div>
            )}
          </div>

          {/* Action row — AssignDifficultyControls on the left, Previous + Next on the right. */}
          <div className="flex flex-wrap items-center gap-3 short:gap-2 mt-auto flex-shrink-0">
            <AssignDifficultyControls
              currentLevel={activeItem.difficultyLevel}
              disabled={inFlight}
              // During the working-set auto-advance window the mask overlay already carries the
              // confirmation, so suppress the redundant inline copy. Direct-link Assign (which
              // never auto-advances) still shows it inline.
              confirmationMessage={autoAdvancing ? null : indicatorConfirmation}
              errorMessage={indicatorAssignError}
              onAssign={onAssign}
            />
            <button
              type="button"
              onClick={onPrev}
              disabled={prevDisabled}
              className={`ml-auto px-4 py-2 short:px-3 short:py-1 short:text-sm rounded-md font-medium ${
                prevDisabled
                  ? 'bg-paper text-ink-soft cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              }`}
            >
              Previous
            </button>
            <button
              type="button"
              onClick={onNext}
              disabled={nextDisabled}
              className={`px-4 py-2 short:px-3 short:py-1 short:text-sm rounded-md font-medium ${
                nextDisabled
                  ? 'bg-paper text-ink-soft cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              }`}
            >
              Next
            </button>
          </div>
        </>
      )}

      {!showNotFound && !activeItem && !loading && (
        <div className="p-4 text-ink-soft">
          {filter === 'NotAssigned'
            ? 'All hands in this filter have been rated.'
            : 'No hands available for this filter.'}
        </div>
      )}
    </div>
  );
}
